import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Provider } from 'react-redux';
import ThemeProvider from './app/theme';
import store from './store/store';
import 'typeface-montserrat';
import { SnackbarProvider, MaterialDesignContent, useSnackbar } from 'notistack';
import './app.css'
import Login from './app/pages/login/login';
import { lazy, Suspense, useEffect } from 'react';
import { Box, CircularProgress, IconButton, styled } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorBoundary from './app/components/ErrorBoundary';
import { AppVersionResume } from './app/hooks/useAppVersionCheck';

const AuthGuard = lazy(() => import('./app/hooks/auth-guard'));
const Dashboard = lazy(() => import('./app/pages/dashboard/dashboard'));
const NotFound404 = lazy(() => import('./app/pages/not-found-404/not-found-404'));

function RouteFallback() {
  return (
    <Box sx={{ width: '100%', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: 'white' }} />
    </Box>
  );
}

function App() {
  useEffect(() => {
    return () => {
      if (window.location.hostname !== "localhost") {
        localStorage.clear();
      }
    };
  }, []);

  const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
    '&.notistack-MuiContent-success': {
      backgroundColor: '#2D7738',
    },
    '&.notistack-MuiContent-error': {
      backgroundColor: '#970C0C',
    },
    '&.notistack-MuiContent-warning': {
      backgroundColor: '#ff9966',
    },
  }));

  function SnackbarCloseButton({ snackbarKey }: any) {
    const { closeSnackbar } = useSnackbar();

    return (
      <IconButton
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
        onClick={() => closeSnackbar(snackbarKey)}>
        <CloseIcon sx={{ color: "white !important" }} />
      </IconButton>
    );
  }


  return (
    <ErrorBoundary>
      <Provider store={store}>
        <ThemeProvider>
          <SnackbarProvider
            action={snackbarKey => <SnackbarCloseButton snackbarKey={snackbarKey} />}
            classes={{ containerRoot: "z-alert" }}
            Components={{
              success: StyledMaterialDesignContent,
              error: StyledMaterialDesignContent,
              warning: StyledMaterialDesignContent
            }}
            maxSnack={3}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}>
            <Router>
              <AppVersionResume />
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route
                    path="/404"
                    element={<NotFound404 />}
                  />
                  <Route path="/login" element={<Login />} />
                  <Route
                    path="*"
                    element={
                      <AuthGuard>
                        <Dashboard />
                      </AuthGuard>
                    }
                  />
                </Routes>
              </Suspense>
            </Router >
          </SnackbarProvider>
        </ThemeProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
